import { Link, createFileRoute } from "@tanstack/react-router";
import { z } from "zod/v4";
import { useForm } from "react-hook-form";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { useMutation } from "@tanstack/react-query";
import { HTTPError } from "ky";
import { useRedirectBack } from "./-utils";
import type { GoogleOauthError } from "@/lib/hooks/google";
import { api } from "@/lib/http";
import { useGoogleOauthMutation } from "@/lib/hooks/google";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/passwordInput";
import { generateDefaultValue } from "@/lib/utils/zod";

export const Route = createFileRoute("/auth/signin")({
  component: RouteComponent,
  staticData: {
    title: "Sign In",
  },
});

const LoginFormSchema = z.object({
  email: z
    .string("Email or username is required")
    .min(1, "Email or username is required"),
  password: z.string("Password is required").min(1, "Password is required"),
});
type LoginFormSchema = z.infer<typeof LoginFormSchema>;

function RouteComponent() {
  const routeSearchParams = Route.useSearch();
  const navigate = Route.useNavigate();

  const redirect = useRedirectBack();

  const form = useForm({
    resolver: standardSchemaResolver(LoginFormSchema),
    defaultValues: generateDefaultValue(LoginFormSchema),
  });

  const login = useMutation({
    mutationFn: async (json: LoginFormSchema) => {
      const searchParams = new URLSearchParams();
      if (routeSearchParams.code)
        searchParams.set("appsumo_code", routeSearchParams.code);
      await api.auth.post("users/login", {
        json,
        searchParams,
      });
    },
    onSuccess() {
      redirect();
    },
    async onError(error: HTTPError, vars) {
      const data = await error.response.json<{
        detail?: string;
        code?: string;
      }>();

      if (data.code === "UNVERIFIED_EMAIL") {
        navigate({
          to: "../verify-account",
          search: {
            email: vars.email,
          },
        });
        return;
      }

      form.setError("root.serverError", {
        message: data.detail || "An unexpected error occurred",
      });
    },
  });

  const googleLogin = useGoogleOauthMutation({
    async onToken(access_token) {
      const searchParams = new URLSearchParams();
      if (routeSearchParams.code)
        searchParams.set("appsumo_code", routeSearchParams.code);
      await api.auth.post("users/social-authentication", {
        json: { access_token, register: false },
        searchParams,
      });
      redirect();
    },
    async onError(error) {
      if (error instanceof HTTPError) {
        const message = await error.response.json<{ detail?: string }>();
        form.setError("root.serverError", {
          message: message.detail || "An unexpected error occurred",
        });
      } else if (
        error &&
        typeof error === "object" &&
        "error_description" in error
      ) {
        form.setError("root.serverError", {
          message: (error as GoogleOauthError).error_description,
        });
      } else {
        form.setError("root.serverError", {
          message: "An unexpected error occurred",
        });
      }
    },
  });

  return (
    <main className="mx-auto flex w-full max-w-md flex-1 flex-col items-center px-4 py-8">
      {/* Custom Tab Bar */}
      <div className="mb-8 w-full max-w-md">
        <div className="flex w-full rounded-2xl bg-gray-50 p-1 shadow-sm dark:bg-[#18181b]">
          <Link
            to="/auth/signup"
            className={
              "flex-1 rounded-xl py-2 text-center text-lg font-semibold transition-all " +
              (window.location.pathname === "/auth/signup"
                ? "bg-white text-gray-600 shadow-md dark:bg-[#232329]"
                : "text-gray-500 hover:text-gray-600 dark:text-gray-400")
            }
          >
            Sign Up
          </Link>
          <Link
            to="/auth/signin"
            className={
              "flex-1 rounded-xl py-2 text-center text-lg font-semibold transition-all " +
              (window.location.pathname === "/auth/signin"
                ? "bg-white text-gray-600 shadow-md dark:bg-[#232329]"
                : "text-gray-500 hover:text-gray-600 dark:text-gray-400")
            }
          >
            Sign In
          </Link>
        </div>
      </div>
      <div className="w-full space-y-6">
        {/* Heading */}
        <div className="space-y-2 text-center">
          <h1 className="text-4xl font-bold">Welcome back!</h1>
          <p className="text-base text-gray-500">Please enter your details.</p>
        </div>

        {/* Form */}
        <Form {...form}>
          <form
            className="space-y-6"
            onSubmit={form.handleSubmit((formData) => {
              if (login.isPending) return;
              login.mutate(formData);
            })}
          >
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {" "}
                    Email <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      autoComplete="email"
                      placeholder="Email"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {" "}
                    Password <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <PasswordInput
                      autoComplete="current-password"
                      placeholder="Password"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Forgot Password */}
            <div className="flex justify-end">
              <Link
                className="text-sm font-medium text-gray-600 hover:underline"
                to="/auth/forgot-password"
              >
                Forgot password?
              </Link>
            </div>

            {/* Sign In Button */}
            <button
              type="submit"
              disabled={login.isPending || googleLogin.isPending}
              className="mb-0 w-full rounded-md bg-gray-700 py-3 font-medium text-white transition-colors hover:bg-gray-600 disabled:opacity-60"
            >
              {login.isPending ? "Signing In..." : "Sign In"}
            </button>
            {/* OR Separator */}
            <div className="my-4 flex items-center">
              <div className="mx-2 h-px flex-1 bg-gray-200 dark:bg-gray-700" />
              <span className="px-2 text-gray-500 dark:text-gray-400">or</span>
              <div className="mx-2 h-px flex-1 bg-gray-200 dark:bg-gray-700" />
            </div>
            {/* Error Message */}
            {form.formState.errors.root?.serverError ? (
              <p className="mt-2 text-sm text-destructive">
                {form.formState.errors.root.serverError.message}
              </p>
            ) : null}

            {/* Google Sign In */}
            <button
              type="button"
              className="flex w-full items-center justify-center gap-2 rounded-md border border-gray-300 py-3 font-medium text-gray-700 transition hover:bg-gray-100 dark:border-gray-700 dark:text-white dark:hover:text-gray-700"
              disabled={login.isPending || googleLogin.isPending}
              onClick={() => {
                if (googleLogin.isPending) return;
                googleLogin.mutate();
              }}
            >
              <img
                src="/icons/google-icon.svg"
                alt="Google logo"
                className="h-5 w-5"
              />
              {googleLogin.isPending ? "Signing In..." : "Sign In with Google"}
            </button>
          </form>
        </Form>

        {/* Sign Up Prompt */}
        <p className="text-center text-sm text-gray-700 dark:text-white">
          Don&apos;t have an account?{" "}
          <Link
            to="/auth/signup"
            search={{ redirect: routeSearchParams.redirect }}
            className="font-medium text-gray-700 hover:underline"
          >
            Sign up
          </Link>
        </p>

        <div className="flex items-center gap-4 rounded-xl border border-gray-200 p-4 dark:border-gray-700">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-gray-200 dark:border-gray-700">
            <img src="/favicon.ico" alt="Chrome" className="h-5 w-5" />
            
          </div>
          <div className="flex-1">
            <div className="text-sm font-semibold text-gray-900 dark:text-white">
              Get the Chrome Extension
            </div>
            <div className="text-xs text-gray-500">
              One-click access to all features in your browser.
            </div>
          </div>
          <a
            href="https://chromewebstore.google.com/detail/polygraf-ai-content-detec/cbngnnhgfljncoliddifmkgklphlojnf"
            target="_blank"
            rel="noopener noreferrer"
            className="shrink-0 rounded-lg bg-gray-700 px-4 py-1.5 text-sm font-medium text-white transition-colors hover:bg-gray-600"
          >
            Install
          </a>
        </div>
      </div>
    </main>
  );
}
